import { useMemo, useState, type ReactNode } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import styled from 'styled-components';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { PDF_DOCUMENT_OPTIONS, PDF_WORKER_SRC } from '@dealstack/frontend/shared/utils/pdfWorker';
import { usePdfDocumentKey } from '@dealstack/frontend/shared/hooks/usePdfDocumentKey';

import type {
	PageDimensionsPt,
	PageRefs,
	ScrollHostRef,
} from '../SignaturePlacement/PlacementCanvas/PlacementCanvas.types.ts';

if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
	pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
}

interface Props {
	file: Blob | string;
	scale: number;
	/**
	 * Page sizes, owned by the CALLER.
	 *
	 * Not held here because every overlay needs them to convert PDF points to browser pixels,
	 * and the placement overlay needs them outside the render too — for the scroll-to-selected
	 * hook and for resolving a stamp dragged across pages. One owner, read by whoever needs it.
	 */
	pageDims: Map<number, PageDimensionsPt>;
	onPageRenderSuccess: (
		pageNum: number,
		page: { originalWidth: number; originalHeight: number }
	) => void;
	onTotalPagesChange?: (totalPages: number) => void;
	/** Attached to the scroller, for a caller that scrolls the selected box into view. */
	scrollHostRef?: ScrollHostRef;
	/** Populated per page, for a caller that needs live page rects. */
	pageRefs?: PageRefs;
	/**
	 * Stops the PDF's own AcroForm values from being painted onto the page.
	 *
	 * For a TEMPLATE those values are somebody else's leftover data, and they land inside the
	 * very rects the binding boxes occupy — label and prefill rendered on top of each other
	 * . Off by default: every other surface shows a real document, not a template.
	 */
	hidePrefilledFormValues?: boolean;
	testId: string;
	pageTestId: (pageNum: number) => string;
	errorText: ReactNode;
	noDataText: ReactNode;
	/**
	 * Drawn over each page, once its size is known.
	 *
	 * Returns the whole overlay rather than its contents: the two overlays this serves differ in
	 * the wrapper itself — the binding one listens for a drag-to-draw and so must take pointer
	 * events, the placement one lets them through to the page beneath. Returning the element
	 * also lets a caller stack MORE than one, which is what puts fields and signatures on a
	 * single document.
	 */
	renderOverlay: (page: { pageNum: number; dims: PageDimensionsPt }) => ReactNode;
}

/**
 * One PDF, rendered once, with somebody else's boxes on top.
 *
 * Field binding and signature placement each used to carry their own copy of this — the same
 * worker setup, the same Document/page column, the same per-page size bookkeeping — which meant
 * a screen showing both parsed and rasterised the same file twice. The overlay is the only part
 * that ever differed, so it is the only part left to the caller.
 */
export const PdfPageCanvas = ({
	file,
	scale,
	pageDims,
	onPageRenderSuccess,
	onTotalPagesChange,
	scrollHostRef,
	pageRefs,
	hidePrefilledFormValues = false,
	testId,
	pageTestId,
	errorText,
	noDataText,
	renderOverlay,
}: Props) => {
	const [numPages, setNumPages] = useState(0);
	const documentKey = usePdfDocumentKey(file);

	const pageNumbers = useMemo(
		() => Array.from({ length: numPages }, (_, index) => index + 1),
		[numPages]
	);

	return (
		<ScrollHost ref={scrollHostRef} data-testid={testId}>
			<Document
				options={PDF_DOCUMENT_OPTIONS}
				key={documentKey}
				file={file}
				onLoadSuccess={({ numPages: loaded }) => {
					setNumPages(loaded);
					onTotalPagesChange?.(loaded);
				}}
				loading={<InlineLoader />}
				error={<div>{errorText}</div>}
				noData={<div>{noDataText}</div>}
			>
				<PageColumn>
					{pageNumbers.map((pageNum) => {
						const dims = pageDims.get(pageNum);
						return (
							<PageWrapper
								key={pageNum}
								ref={(element) => {
									if (pageRefs) {
										pageRefs.current[pageNum] = element;
									}
								}}
								data-testid={pageTestId(pageNum)}
							>
								<Page
									pageNumber={pageNum}
									scale={scale}
									renderTextLayer={false}
									renderAnnotationLayer={false}
									renderForms={hidePrefilledFormValues}
									onRenderSuccess={(page) =>
										onPageRenderSuccess(pageNum, {
											originalWidth: page.originalWidth,
											originalHeight: page.originalHeight,
										})
									}
								/>
								{dims && renderOverlay({ pageNum, dims })}
							</PageWrapper>
						);
					})}
				</PageColumn>
			</Document>
		</ScrollHost>
	);
};

const ScrollHost = styled.div`
	background-color: ${({ theme }) => theme.v3.colors.gray[1]};
	overflow: auto;
	flex-grow: 1;
	display: flex;
	justify-content: center;
	padding: 1rem 0;
`;

const PageColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const PageWrapper = styled.div`
	position: relative;
`;
